import { FileProtectOutlined, WarningFilled } from '@ant-design/icons';
import {
  EnumUpdateStatoRichiestaRequestStato,
  PagamentoWithDettaglioDTO,
  RichiestaDTO,
  getGetFrontRichiesteIdQueryKey,
  usePostFrontRichiesteIdPagamentiIdPagamentoEbollo,
  usePutFrontRichiesteIdStato,
} from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import { EnumCodStatoPagamento } from '@framework/frontoffice-submission';
import { useTranslation } from '@framework/i18n';
import { ModalDialog } from '@framework/ui';
import { useQueryClient } from 'react-query';
import { Button, message, Space, Tooltip } from 'antd';
import { useState } from 'react';
import styled from 'styled-components';
import { isPaymentLockedToDifferentMethod } from './isPaymentLockedToMethod';

const WarningIcon = styled(WarningFilled)`
  color: ${props => props.theme.colorWarning};
`;

interface StartStampDutyPaymentButtonProps {
  payment: PagamentoWithDettaglioDTO;
  submission: RichiestaDTO;
}

export function StartStampDutyPaymentButton(
  props: StartStampDutyPaymentButtonProps,
) {
  const { payment, submission } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const startPaymentQuery = usePostFrontRichiesteIdPagamentiIdPagamentoEbollo({
    mutation: {
      onSuccess: response => {
        setIsRedirecting(true);
        message.info(
          t('stamp-duty-payment.avvio-pagamento', `Avvio del pagamento...`),
        );
        window.location.href = response;
      },
      onError: e => {
        queryClient.invalidateQueries(
          getGetFrontRichiesteIdQueryKey(submission.id!),
        );
        message.error(
          getNetworkErrorMessage(
            e,
            t(
              'stamp-duty-payment.errore-avvio-pagamento',
              "Si è verificato un errore durante l'avvio del pagamento",
            ),
          ),
        );
      },
    },
  });
  const updateSubmissionStateQuery = usePutFrontRichiesteIdStato({
    mutation: {
      onSuccess: response => {
        startPayment();
      },
      onError: e => {
        message.error(
          getNetworkErrorMessage(
            e,
            t(
              'stamp-duty-payment.errore-modifica-stato-pagamento',
              `Si è verificato un errore durante la modifica dello stato del pagamento`,
            ),
          ),
        );
      },
    },
  });

  const startPayment = () => {
    startPaymentQuery.mutate({
      id: payment.idRichiesta!,
      idPagamento: payment.id!,
      data: {
        urlIndietro: generatePaymentCallbackUrl('esitoBollo', 'indietro'),
        urlRitorno: generatePaymentCallbackUrl('esitoBollo', 'successo'),
      },
    });
  };

  const handleClick = () => {
    // Se la richiesta è in pagamento ("PAGAMENTO") o è già stata inviata
    // ("INVIATA", "RICEVUTA", "INTEGRATA", "CONCLUSA"), non è necessario
    // invalidare le marche precedenti e bloccare la richiesta,
    // in quanto già immodificabile.
    if (submission?.stato !== EnumUpdateStatoRichiestaRequestStato.BOZZA) {
      startPayment();
      return;
    }
    setModalOpen(true);
  };

  const handleConfirm = () => {
    setModalOpen(false);
    updateSubmissionStateQuery.mutate({
      id: payment.idRichiesta!,
      data: {
        stato: EnumUpdateStatoRichiestaRequestStato.PAGAMENTO,
      },
    });
  };

  if (isPaymentLockedToDifferentMethod(payment, 'BOLLO')) return null;

  if (payment.codStatoPagamento === EnumCodStatoPagamento.SUCCESSO) return null;

  // Blocchiamo i pagamenti in errore poiché GovPay non permette di ripetere i pagamenti
  if (payment.codStatoPagamento === EnumCodStatoPagamento.FALLIMENTO)
    return null;

  if (
    !payment.oneri ||
    payment.oneri.length === 0 ||
    payment.oneri.some(o => !o.tipoOnere?.flPagamentoBollo)
  ) {
    return null;
  }

  return (
    <>
      <Tooltip
        title={t(
          'form.procedi-al-pagamento-bollo',
          'Procedi al pagamento del Bollo',
        )}
      >
        <Button
          icon={
            <FileProtectOutlined
              aria-label={t('form.button.paga-il-bollo', 'Paga il Bollo')}
            />
          }
          onClick={handleClick}
          disabled={!payment.id}
          loading={
            startPaymentQuery.isLoading ||
            updateSubmissionStateQuery.isLoading ||
            isRedirecting ||
            payment.codStatoPagamento === EnumCodStatoPagamento.PENDENTE
          }
        />
      </Tooltip>

      <ModalDialog
        open={modalOpen}
        title={
          <Space>
            <WarningIcon />
            {t('form.conferma', 'Conferma')}
          </Space>
        }
        onOk={handleConfirm}
        onCancel={() => setModalOpen(false)}
        width={600}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {t(
            'form.applicazione-marca-da-bollo',
            'Applicando la marca da bollo, la richiesta verrà congelata e non sarà possibile applicare modifiche.',
          )}
          <br />
          {t('form.conferma-di-procedere', 'Confermi di voler procedere?')}
        </Space>
      </ModalDialog>
    </>
  );
}

export function generatePaymentCallbackUrl(
  qsName?: string,
  qsValue?: string,
  sourceHref: string = window.location.href,
) {
  const url = new URL(sourceHref);
  // Reimpostiamo i parametri della query string, per evitare che vengano
  // propagati alla nuova URL
  url.search = '';
  if (qsName && qsValue) {
    url.searchParams.set(qsName, qsValue);
  }
  return url.href;
}
